import { useCreateWarrantyContractSkuMutation } from '@/services/warranties';
import {
  Box,
  Button,
  Modal,
  SelectInputNative,
  TextInput,
  TextareaInput,
  toast,
} from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ProgramType, WarrantyProvider } from 'types';

const PROGRAM_TYPE_OPTIONS = [
  { label: 'HVAC', value: ProgramType.hvac },
  { label: 'Water Heater', value: ProgramType.waterHeater },
];

const PROVIDER_OPTIONS = [{ label: 'OnPoint Warranty', value: WarrantyProvider.onPointWarranty }];

interface ContractSkuModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContractSkuModal = ({ isOpen, onClose }: ContractSkuModalProps) => {
  const [createSku, { isLoading }] = useCreateWarrantyContractSkuMutation();

  type FormValues = {
    warrantyProvider: WarrantyProvider;
    programType: ProgramType;
    term: string;
    contractSku: string;
    payoutAmount: string;
    displayName: string;
    description: string;
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      warrantyProvider: WarrantyProvider.onPointWarranty,
      programType: ProgramType.hvac,
      term: '10',
      contractSku: '',
      payoutAmount: '',
      displayName: '',
      description: '',
    },
  });

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const handleFormSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await createSku({
          ...values,
          term: Number(values.term),
          payoutAmount: values.payoutAmount !== '' ? Number(values.payoutAmount) : undefined,
        }).unwrap();
        toast.success('Contract SKU created');
        reset();
        onClose();
      } catch {
        toast.error('Failed to create SKU');
      }
    },
    [createSku, reset, onClose],
  );

  return (
    <Modal ariaLabeledBy="contractSkuModal" isOpen={isOpen} onDismiss={handleClose} maxWidth="500px">
      <Modal.Header id="contractSkuModal" title="Add Contract SKU" onDismiss={handleClose} />
      <Modal.Body overflow="auto" maxHeight="70vh">
        <Box direction="column" gap="md">
          <Controller
            name="warrantyProvider"
            control={control}
            rules={{ required: 'Provider is required' }}
            render={({ field }) => (
              <SelectInputNative
                label="Provider"
                id="warrantyProvider"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={PROVIDER_OPTIONS}
                error={errors.warrantyProvider?.message}
              />
            )}
          />

          <Controller
            name="programType"
            control={control}
            rules={{ required: 'Program type is required' }}
            render={({ field }) => (
              <SelectInputNative
                label="Program Type"
                id="programType"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={PROGRAM_TYPE_OPTIONS}
                error={errors.programType?.message}
              />
            )}
          />

          <Controller
            name="term"
            control={control}
            rules={{
              required: 'Term is required',
              validate: (v) =>
                (Number.isInteger(Number(v)) && Number(v) >= 1) || 'Term must be a positive whole number',
            }}
            render={({ field }) => (
              <TextInput
                label="Term (years)"
                id="term"
                type="number"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                errorMessage={errors.term?.message}
              />
            )}
          />

          <Controller
            name="contractSku"
            control={control}
            rules={{ required: 'Contract SKU is required' }}
            render={({ field }) => (
              <TextInput
                label="Contract SKU"
                id="contractSku"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                errorMessage={errors.contractSku?.message}
                helpText="This must exactly match SKU from Warranty Provider"
              />
            )}
          />

          <Controller
            name="payoutAmount"
            control={control}
            render={({ field }) => (
              <TextInput
                label="Palmetto Reimbursement Amount ($)"
                id="payoutAmount"
                type="number"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />

          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextInput
                label="Display Name"
                id="displayName"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextareaInput
                id="description"
                label="Description"
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                rows={3}
              />
            )}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" gap="sm" justifyContent="flex-end">
          <Button as="button" variant="secondary" onClick={handleClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            as="button"
            variant="primary"
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isLoading}
            isDisabled={isLoading}
          >
            Create
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};
